<script setup lang="ts">
import type { InferFormValidatorSchema } from "maz-ui";
import { string, nonEmpty, pipe, email, maxLength } from "valibot";
import { useFormValidator } from "maz-ui";
import { useRoute } from "vue-router";
import { getDropdownVehicleType } from "@/api/dropdown";
import { addAds } from "~/api/ads";

const gtm: any = useGtm();
const route = useRoute();
const router = useRouter();
const utmSource = route.query.utm_source;
const loading = ref(false);
const showSuccess = ref(false);
const query = route.query;
const isOpen = ref(false);

const ddVehicleTypes = ref<string[]>([]);
const utm_source = computed(() => utmSource);

onMounted(async () => {
  const res = await getDropdownVehicleType();
  ddVehicleTypes.value = res.data;
});

const defaultSchema = {
  name: pipe(string(), nonEmpty("Name is required")),
  phone: pipe(string(), nonEmpty("Phone is required")),
  // vehicle_type: pipe(any(), nonEmpty("Motor bike type is required")),
  email: pipe(string(), nonEmpty("Email is required")),
};

const defaultFieldStates = {
  name: "",
  phone: "",
  email: "",
};

const schema = ref<any>(defaultSchema);
type Model = InferFormValidatorSchema<typeof schema>;

const { model, isSubmitting, handleSubmit, errorMessages, fieldsStates } =
  useFormValidator<Model>({
    schema,
    defaultValues: defaultFieldStates,
    options: { mode: "lazy", scrollToError: ".has-error" },
  });

const closeSuccess = () => {
  schema.value = {
    name: string(),
    phone: string(),
    vehicle_type: string(),
    email: email(),
  };
  model.value = { ...defaultFieldStates };
  setTimeout(() => {
    schema.value = { ...defaultSchema };
  }, 100);
  window.scrollTo({ top: 0, behavior: "smooth" });
  showSuccess.value = false;
};

const setDialog = () => (isOpen.value = true);

const onSubmit = handleSubmit(async (formData) => {
  let submitData = <any>{};
  Object.assign(submitData, formData);

  try {
    loading.value = true;
    const response = await addAds({
      ...submitData,
      ads_source: utm_source.value || "",
    });

    if (response.status) {
      isOpen.value = false;
      gtm.trackEvent({
        event: "submit-data-ads",
        action: "click",
        value: 1,
      });
      router.replace({
        path: "/bike",
        query: { ...query, ...formData },
      });
    }
  } catch (error) {
    console.error("Failed to submit data ads:", error);
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="bg-primary rounded-xl p-4 w-max text-white cursor-pointer mx-auto" @click="setDialog"> Book Now! </div>

  <MazDialog
    v-model="showSuccess"
    persistent
    class="text-center"
    id="dialog-ads-form"
  >
    <Icon name="icon-park-solid:success" size="45" class="text-primary mb-2" />
    <div class="text-2xl text-primary font-semibold mb-1">Congratulations,</div>
    <p>Your data was successfully submited!</p>
    <div class="flex justify-center mt-4">
      <MazBtn @click="closeSuccess()" color="primary"> Finish </MazBtn>
    </div>
  </MazDialog>

  <MazDialog
    :model-value="isOpen"
    @update:model-value="isOpen = $event"
    width="400"
  >
    <template #title>
      <div class="flex items-center text-base">
        <Icon
          name="carbon:checkmark-filled"
          class="mr-2 w-5 h-5 bg-primary"
          width="25"
          height="25"
        />
        Booking Now
      </div>
    </template>

    <div class="">
      <form
        :style="{ display: showSuccess ? 'none' : 'block' }"
        @submit.prevent="onSubmit"
      >
        <div class="flex flex-col gap-4">
          <MazInput
            v-model="model.name"
            label="Your Name"
            autocomplete="off"
            :hint="errorMessages.name"
            :error="!!errorMessages.name"
            :success="fieldsStates.name.valid"
            :class="{ 'has-error': !!errorMessages.name }"
            class="w-full"
          />
          <MazInput
            v-model="model.email"
            label="Your Email"
            autocomplete="off"
            :hint="errorMessages.email"
            :error="!!errorMessages.email"
            :success="fieldsStates.email.valid"
            :class="{ 'has-error': !!errorMessages.email }"
            class="w-full"
          />
          <MazPhoneNumberInput
            v-model="model.phone"
            id="input-phone-data"
            :hint="errorMessages.phone"
            :error="!!errorMessages.phone"
            :success="fieldsStates.phone.valid"
            :class="{ 'has-error': !!errorMessages.phone }"
            label="Whatsapp Number"
            default-country-code="ID"
          />

          <MazBtn
            id="btn-reservation-submit"
            :loading="isSubmitting"
            :disabled="loading"
            type="submit"
            class="w-full"
          >
            <span>Book</span>
          </MazBtn>
        </div>
      </form>
    </div>
  </MazDialog>
</template>
